import React from 'react';
import { Box, Tabs, Tab, Typography, Grid, Card, CardContent, CardActionArea } from '@mui/material';
import { Form990Generator } from './Form990';
import { Form990Provider } from '../../contexts/Form990Context';
import { PreviewDisplay } from './Form990/components/Preview/PreviewDisplay';
import { useForm990Data } from './Form990/hooks/useForm990Data';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const reportTypes = [
  {
    id: 'form990',
    title: 'Form 990',
    description: 'Generate and file annual Form 990 tax returns',
    icon: <DescriptionIcon sx={{ fontSize: 40, color: '#4FD1C5' }} />,
    component: Form990Generator
  },
  {
    id: 'annual-report',
    title: 'Annual Report',
    description: 'Create comprehensive annual organizational reports',
    icon: <AssessmentIcon sx={{ fontSize: 40, color: '#4FD1C5' }} />
  },
  {
    id: 'statement-of-activity',
    title: 'Statement of Activity',
    description: 'Generate detailed financial activity statements',
    icon: <ReceiptIcon sx={{ fontSize: 40, color: '#4FD1C5' }} />
  },
  {
    id: 'financial-reports',
    title: 'Financial Reports',
    description: 'Access various financial and accounting reports',
    icon: <AccountBalanceIcon sx={{ fontSize: 40, color: '#4FD1C5' }} />
  }
];

export const Reports: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  
  // Fix the useForm990Data hook usage
  // 1. Provide the required parameters (ein and year)
  // 2. Rename the destructured properties to match what the hook returns
  const { data: form990Data, loading: isLoading, error } = useForm990Data('123456789', 2023);
  
  // Add a regenerate990 function since it's used in the component
  const regenerate990 = async () => {
    // Implementation for regenerating Form 990 data
    console.log('Regenerating Form 990 data...');
    // You might want to call a refresh function from the hook if available
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Dashboard" />
          <Tab label="Form 990" />
          <Tab label="Financial Reports" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          <Grid container spacing={3}>
            {reportTypes.map((report) => (
              <Grid item xs={12} sm={6} md={3} key={report.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    backgroundColor: '#1E2A3B',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      border: '1px solid #4FD1C5',
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => setActiveTab(report.id === 'form990' ? 1 : 2)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      textAlign: 'center',
                      height: '100%',
                      p: 3
                    }}>
                      {report.icon}
                      <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#FFFFFF' }}>
                        {report.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {report.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {activeTab === 1 && (
        <Form990Provider>
          <Form990Generator onClose={() => {/* Handle close */}} />
        </Form990Provider>
      )}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h5" gutterBottom>
            Financial Reports
          </Typography>
        </Box>
      )}
    </Box>
  );
}; 